import { useId, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { ACCESS_LINK_PATH, type TAdminUser, type TIssuedAccessLink } from '@schemas'

import { useAuthOptions, useEmailAccessLink, usePeopleSearch } from '@/api/hooks'
import { parseApiErrorMessage } from '@/lib/api-errors'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export function formatDate(value?: string | null) {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleString()
}

/** Invited accounts show whether their setup link still works. */
export function AccountStatusBadge({ user }: { user: TAdminUser }) {
  if (user.status === 'disabled')
    return (
      <Badge variant="destructive">
        {user.hasPassword ? 'Disabled' : 'Disabled · never set up'}
      </Badge>
    )
  if (user.status === 'active') return <Badge variant="secondary">Active</Badge>
  const expires = user.accessLinkExpiresAt ? new Date(user.accessLinkExpiresAt) : null
  return (
    <Badge variant="outline">
      {expires && expires.getTime() > Date.now()
        ? `Invited · link until ${expires.toLocaleDateString()}`
        : 'Invited · link expired'}
    </Badge>
  )
}

export function accessLinkUrl(token: string) {
  return `${window.location.origin}${ACCESS_LINK_PATH}#token=${token}`
}

/**
 * A link shown once, right after it is issued; it cannot be shown again later. Where the server
 * sends email, it can also go to the account's own address.
 */
export function AccessLinkPanel({
  link,
  userId,
  email,
}: {
  link: TIssuedAccessLink
  userId: string
  email: string
}) {
  const inputId = useId()
  const url = accessLinkUrl(link.token)
  const options = useAuthOptions()
  const emailLink = useEmailAccessLink()
  // The token emailed, so a newer link shown in its place can be emailed too.
  const [emailedToken, setEmailedToken] = useState<string | null>(null)
  const emailed = emailedToken === link.token
  const send = async () => {
    try {
      const { sentTo } = await emailLink.mutateAsync({ userId, token: link.token })
      setEmailedToken(link.token)
      toast.success(`Link emailed to ${sentTo}.`)
    } catch (error) {
      toast.error(parseApiErrorMessage(error))
    }
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied.')
    } catch {
      toast.error('Copy the link from the box instead.')
    }
  }
  return (
    <div className="space-y-2 rounded-md border bg-muted/40 p-3" data-testid="access-link-panel">
      <label htmlFor={inputId} className="text-sm font-medium">
        {link.kind === 'setup' ? 'Setup link' : 'Password reset link'} for {email}
      </label>
      <div className="flex gap-2">
        <Input
          id={inputId}
          readOnly
          value={url}
          onFocus={event => event.currentTarget.select()}
          className="font-mono text-xs"
        />
        <Button type="button" variant="outline" onClick={copy}>
          Copy
        </Button>
      </div>
      {options.data?.accountEmail ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={emailLink.isPending || emailed}
          onClick={send}
        >
          {emailed ? `Emailed to ${email}` : `Email the link to ${email}`}
        </Button>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Works once and expires {formatDate(link.expiresAt)}. Share it only with this person; a newer
        link replaces it. It is not shown again after you close this.
      </p>
    </div>
  )
}

export type PickedPerson = { id: string; name: string; email?: string | null }

export function PersonPicker({
  selected,
  onSelect,
}: {
  selected: PickedPerson | null
  onSelect: (person: PickedPerson | null) => void
}) {
  const inputId = useId()
  const [query, setQuery] = useState('')
  const term = query.trim()
  const people = usePeopleSearch({ q: term, pageSize: 20 }, { enabled: term.length >= 2 })
  return (
    <div className="space-y-2">
      <label htmlFor={inputId} className="text-sm font-medium">
        Search people
      </label>
      <Input
        id={inputId}
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder="Name or email (at least 2 characters)"
      />
      {people.isError && <p role="alert">Unable to search people. Try again.</p>}
      <div className="max-h-56 space-y-1 overflow-auto">
        {term.length >= 2 &&
          (people.data?.people ?? []).map(candidate => (
            <Button
              key={candidate.id}
              type="button"
              variant="outline"
              className="h-auto w-full justify-start whitespace-normal text-left"
              onClick={() =>
                candidate.id &&
                onSelect({ id: candidate.id, name: candidate.name, email: candidate.email })
              }
              aria-pressed={selected?.id === candidate.id}
            >
              {candidate.name}
              {candidate.email ? ` — ${candidate.email}` : ''}
            </Button>
          ))}
        {term.length >= 2 && !people.isFetching && people.data?.people.length === 0 && (
          <p className="text-sm">No people found.</p>
        )}
      </div>
      <p className="text-sm">
        Selected:{' '}
        {selected
          ? `${selected.name}${selected.email ? ` (${selected.email})` : ''}`
          : 'No person linked'}
      </p>
      {selected && (
        <Button type="button" variant="outline" size="sm" onClick={() => onSelect(null)}>
          Remove person link
        </Button>
      )}
    </div>
  )
}

export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  pending,
  onConfirm,
  onCancel,
}: {
  title: string
  children: ReactNode
  confirmLabel: string
  pending: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <Dialog open onOpenChange={open => !open && !pending && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{children}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={pending}>
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
